/**********************************************************************************************************
 *   BASE IMPORT
 **********************************************************************************************************/
import styled from 'styled-components'
import { CombinedTButtonProps, TAnchorButtonProps, TLinkButtonProps, TOnClickButtonProps, TSubmitButtonProps, _Button } from '../base'

/**********************************************************************************************************
 *   UTILITIES
 **********************************************************************************************************/
import { Theme } from 'nxui/src'
import { forwardRef, type HTMLAttributes } from 'react'
import { ButtonStyles } from './style'

/**********************************************************************************************************
 *   TYPE DEFINITIONS
 **********************************************************************************************************/
// For the button variation (non-anchor) allow size property
type TButtonProps = CombinedTButtonProps & { size?: 'small' | 'medium' | 'large'; align: 'left' | 'center' | 'right' }

// Helper to easily define a button
type Button<T extends { type: string }> = React.ForwardRefExoticComponent<
    Omit<T, 'type'> &
        React.RefAttributes<HTMLElement | ((el: HTMLElement) => void)> & {
            size?: 'small' | 'medium' | 'large'
            intrinsic?: HTMLAttributes<HTMLElement>
        }
>

// Define the button variants (without type as the component defines this)
type StandardButton = Button<TOnClickButtonProps>
type SubmitButton = Button<TSubmitButtonProps>
type AnchorButton = Button<TAnchorButtonProps>
type LinkButton = Button<TLinkButtonProps>

/**********************************************************************************************************
 *   STYLES
 **********************************************************************************************************/
const StyledButton = styled(_Button)<TButtonProps>`
    display: inline-flex;
    align-items: center;
    width: fit-content;
    white-space: nowrap;
    border-radius: 8px;
    background-size: cover;
    font-size: 1.4rem;
    text-decoration: none;
    letter-spacing: -0.25px;
    line-height: 1;

    ${Theme.Functions.setFont('Inter', 500)};
    ${Theme.Functions.animation('all')};
    ${({ fullwidth }) => (fullwidth ? 'width: 100%;' : '')}
    ${({ align }) => (align ? `justify-content: ${align};` : '')}

    .icon {
        width: 16px;
        height: 16px;
        margin: 0 6px 0 -4px;
        ${Theme.Functions.animation('all')};
    }

    ${ButtonStyles.Color}
    ${ButtonStyles.Size}
`

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export const _StandardButton: StandardButton = forwardRef((props, ref) => {
    return (
        <StyledButton type='onclick' ref={ref} {...(props as ExpectedAny)}>
            {props.children}
        </StyledButton>
    )
})

export const _SubmitButton: SubmitButton = forwardRef((props, ref) => {
    return (
        <StyledButton type='submit' ref={ref} {...(props as ExpectedAny)}>
            {props.children}
        </StyledButton>
    )
})

export const _AnchorButton: AnchorButton = forwardRef((props, ref) => {
    return (
        <StyledButton type='anchor' ref={ref} {...(props as ExpectedAny)}>
            {props.children}
        </StyledButton>
    )
})

export const _LinkButton: LinkButton = forwardRef((props, ref) => {
    return (
        <StyledButton type='link' ref={ref} {...(props as ExpectedAny)}>
            {props.children}
        </StyledButton>
    )
})
